/**
 * «Мои объявления» о грузе — блок `WEB-2.1`, требования 302, 306 и 314.
 *
 * Экран показывает **каждое** объявление фирмы из адреса так, как его отдал
 * сервер, и в том порядке, в каком отдал: маршрут, дату погрузки, статус
 * подписью, «свободно N из M» и километраж. Ни отбора, ни пересортировки —
 * закрытое и занятое объявления остаются в списке со своими подписями, а
 * порядок задаёт курсор сервера. Страницы дописываются **после** прежних, без
 * отбора повторов (поправка CONTRACT `К1`).
 *
 * Чьи кнопки показывать, экран не решает. «Изменить» и «Закрыть» стоят ровно
 * там, где сервер сказал `manageable: true`; «Создать объявление» — ровно там,
 * где у членства фирмы из адреса `can_manage_cargo_listings: true` (решение
 * формы `Б2`, серверное требование 549). Ни роли, ни типа фирмы экран ради
 * этого не читает: логист транспортной фирмы публикует так же, как логист
 * логистической, а право считает сервер. Интерфейс не граница безопасности —
 * закрытие без права сервер отклонит, и отказ ляжет текстом.
 *
 * Состояние встаёт по единственному правилу места (долг `TD-66`): пустой
 * ответ — на месте списка, заголовок и «Создать» видны; ответа нет — на месте
 * экрана, и отказ прав с отсутствием фирмы дают побайтово один экран.
 */

import { ApiFailure } from '../api/client';
import type { ApiClient } from '../api/client';
import { membershipsIn, nextPageCursor } from '../api/contract';
import type { CargoListing, Membership } from '../api/contract';
import { activeLocale, listingStatusStringKey } from '../i18n';
import type { Strings } from '../i18n';
import { cargoListingEditPath, cargoListingNewPath, cargoListingPath } from '../router';
import { dateLabel, enumLabel, placeState, refusalText, screenStateFor } from './state';
import type { ScreenState } from './state';

export interface CargoListingsScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
}

export interface CargoListingsScreen {
  readonly element: HTMLElement;
  /** Состояние экрана либо `null` — содержимое показано. */
  readonly state: ScreenState | null;
  readonly listings: readonly CargoListing[];
  /** Чтение с начала: профиль ради признака членства и первая страница. */
  load(): Promise<void>;
  /** Следующая страница по курсору предыдущей. Без курсора — ничего. */
  loadMore(): Promise<void>;
}

export function createCargoListingsScreen(options: CargoListingsScreenOptions): CargoListingsScreen {
  const { client, strings, companyId } = options;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const createLink = document.createElement('a');
  const notice = document.createElement('p');
  const list = document.createElement('ul');
  const more = document.createElement('button');

  element.className = 'screen screen-cargo-listings';
  heading.textContent = strings.cargo_listings_title;

  createLink.className = 'company-link';
  createLink.href = cargoListingNewPath(companyId);
  createLink.textContent = strings.cargo_listings_create;

  notice.className = 'notice';
  notice.setAttribute('role', 'alert');

  more.type = 'button';
  more.className = 'action';
  more.textContent = strings.cargo_listings_more;

  let state: ScreenState | null = 'loading';
  /** Пришёл ли ответ сервера — величина правила места состояния. */
  let answered = false;
  let listings: readonly CargoListing[] = [];
  let cursor: string | null = null;
  /** Следующая страница в пути — та же страница не просится дважды (Р6). */
  let loadingMore = false;
  /**
   * Закрытие в пути — до ответа сервера кнопки «Закрыть» неактивны, второе
   * нажатие второго запроса не шлёт (находка ревью Р6).
   */
  let closing = false;
  /** Признак членства фирмы из адреса — как его посчитал сервер. */
  let publisher = false;

  /**
   * Вправе ли смотрящий публиковать в этой фирме — **ответ сервера**, а не
   * вывод клиента: признак считается на каждый ответ профиля по правам роли,
   * персональным разрешениям и запретам. Записей членства в одной фирме может
   * прийти несколько (закрытая и действующая) — у закрытой признак ложен
   * сервером, и здесь он только читается.
   */
  function publishes(memberships: readonly Membership[]): boolean {
    for (const membership of membershipsIn(memberships, companyId)) {
      if (membership.can_manage_cargo_listings) {
        return true;
      }
    }

    return false;
  }

  function row(listing: CargoListing): HTMLElement {
    const node = document.createElement('li');
    const route = document.createElement('a');
    const facts = document.createElement('span');

    node.className = 'cargo-listing-row';

    route.className = 'company-link';
    route.href = cargoListingPath(companyId, listing.id);
    route.textContent = `${listing.origin} → ${listing.destination}`;

    // Всё в строке — поля ответа: дату и километраж считает и хранит сервер,
    // «свободно N из M» — его же два числа. Статус — подписью строителя;
    // значение вне перечня получает «неизвестно» без исходной строки (CR-007).
    const shown = [
      dateLabel(activeLocale(), listing.loading_at),
      enumLabel(strings, listingStatusStringKey(listing.status)),
      `${strings.cargo_listing_slots_free}: ${listing.slots_free} ${strings.cargo_listing_slots_of} ${listing.slots_total}`,
      `${listing.distance_km} ${strings.cargo_listing_km}`,
    ];

    // Фиксированная цена — пометкой строки: торг разрешён по умолчанию
    // (требование 318), и строку без торга логист узнаёт в списке сразу.
    if (!listing.negotiable) {
      shown.push(strings.cargo_listing_fixed);
    }

    facts.textContent = shown.join(' · ');

    node.append(route, facts);

    if (listing.manageable) {
      const edit = document.createElement('a');
      const close = document.createElement('button');

      edit.className = 'company-link';
      edit.href = cargoListingEditPath(companyId, listing.id);
      edit.textContent = strings.cargo_listing_edit;

      close.type = 'button';
      close.className = 'action action--danger';
      close.textContent = strings.cargo_listing_close;
      close.disabled = closing;
      close.addEventListener('click', () => {
        void closeListing(listing.id);
      });

      node.append(edit, close);
    }

    return node;
  }

  function render(): void {
    const rows: HTMLElement[] = [];

    for (const listing of listings) {
      rows.push(row(listing));
    }

    list.replaceChildren(...rows);

    const frame: HTMLElement[] = [heading];

    if (publisher) {
      frame.push(createLink);
    }

    frame.push(notice);

    placeState(
      element,
      {
        state,
        answered,
        frame,
        list,
        after: cursor === null ? [] : [more],
        emptyCaption: strings.cargo_listings_empty,
      },
      strings,
    );
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    answered = false;
    listings = [];
    cursor = null;
    publisher = false;
    notice.textContent = '';
    render();

    try {
      const [profile, page] = await Promise.all([
        client.getCurrentUser(),
        client.listCompanyCargoListings(companyId),
      ]);

      publisher = publishes(profile.memberships);
      listings = page.data;
      cursor = nextPageCursor(page.meta);
      answered = true;
      state = screenStateFor({ kind: 'done', empty: listings.length === 0 });
    } catch (error) {
      listings = [];
      cursor = null;
      answered = false;
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  /**
   * Перечитать список после действия — первая страница заново, а не правка
   * строки клиентом: статус закрытого объявления и «свободно N из M» считает
   * сервер. Отказ перечитывания не уносит показанного: строки уже пришли и
   * верны, отказ ложится текстом.
   */
  async function refresh(): Promise<void> {
    try {
      const page = await client.listCompanyCargoListings(companyId);

      listings = page.data;
      cursor = nextPageCursor(page.meta);
      answered = true;
      state = screenStateFor({ kind: 'done', empty: listings.length === 0 });
    } catch (error) {
      notice.textContent = refusalText(error, strings);
    }

    render();
  }

  async function loadMore(): Promise<void> {
    if (cursor === null || loadingMore) {
      return;
    }

    loadingMore = true;
    more.disabled = true;

    try {
      const page = await client.listCompanyCargoListings(companyId, { cursor });

      // Страница встаёт **после** прежних в порядке ответа: ни пересортировки,
      // ни отбора повторов — порядок держит курсор сервера.
      listings = [...listings, ...page.data];
      cursor = nextPageCursor(page.meta);
    } catch (error) {
      // Полученное остаётся на экране, курсор не теряется: сбой следующей
      // страницы не делает предыдущие неверными.
      notice.textContent = refusalText(error, strings);
    } finally {
      loadingMore = false;
      more.disabled = false;
    }

    render();
  }

  async function closeListing(listingId: string): Promise<void> {
    if (closing || !window.confirm(strings.cargo_listing_close_confirm)) {
      return;
    }

    closing = true;
    notice.textContent = '';
    render();

    try {
      try {
        await client.closeCargoListing(listingId);
      } catch (error) {
        // Отказ — текстом сервера на месте действия (решение формы `Б6`),
        // экран остаётся на списке. Ответ сервера был — список
        // перечитывается: что с объявлением на самом деле, знает он.
        notice.textContent = refusalText(error, strings);

        if (!(error instanceof ApiFailure) || error.code === null) {
          return;
        }
      }

      await refresh();
    } finally {
      closing = false;
      render();
    }
  }

  more.addEventListener('click', () => {
    void loadMore();
  });

  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    get listings(): readonly CargoListing[] {
      return listings;
    },

    load,
    loadMore,
  };
}
